import { useParams } from 'react-router-dom'
import { buildErrorMessage, documentationRootGuid, projectFilesRootGuid } from '@constants'
import { getShapeFromElement } from '@editorUtils'
import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useGuidToCrossSection, useGuidToElementIncludingRips } from '@editorHooks'
import { getProjectFiles, getProjectFilesTemplates } from '@queries'
import { saveProjectFileItem } from '@mutations'

const useUploadProjectDocumentationFile = (
  rootGuid: string,
  onUploadDocument?: (data: FileItem) => void,
) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const guidToElement = useGuidToElementIncludingRips()
  const guidToCrossSection = useGuidToCrossSection()

  return useMutation(
    (data: FileItem & { file: File }) => {
      const enhancedAdditionalData = data.additional_data || {}

      enhancedAdditionalData.begin_on_new_page = true

      if (data.element_guid) {
        const shape = getShapeFromElement(guidToElement[data.element_guid])

        enhancedAdditionalData._elementData = {
          shape,
          crossSection: guidToCrossSection[data.element_guid],
        }
      }

      const enhancedData = {
        ...data,
        additional_data: enhancedAdditionalData,
      }

      return saveProjectFileItem.request(projectId, rootGuid, enhancedData)
    },
    {
      onSuccess: async (data: FileItem) => {
        enqueueSnackbar('Erfolgreich hochgeladen', { variant: 'success' })

        const filesKey = getProjectFiles.getKey(projectId, rootGuid)
        // A refetch started before the upload was saved would overwrite the new file
        // with the tree it read before, until the next refetch
        await queryClient.cancelQueries(filesKey)
        // The files of the root are only cached once loaded (e.g. not on the Results page)
        queryClient.setQueryData<FilesResult | undefined>(
          filesKey,
          currentData => currentData && { ...currentData, files: [...currentData.files, data] },
        )

        // The uploaded file is stored as project file ("Dateien"), which the element
        // "Dokumente" lists show: add it right away, the refetch below completes it
        const { attachment } = data
        if (attachment) {
          const projectFilesKey = getProjectFiles.getKey(projectId, projectFilesRootGuid)
          await queryClient.cancelQueries(projectFilesKey)
          queryClient.setQueryData<FilesResult | undefined>(
            projectFilesKey,
            currentData =>
              currentData && {
                ...currentData,
                files: [
                  ...currentData.files,
                  {
                    guid: attachment.guid,
                    name: attachment.name,
                    mimetype: attachment.mimetype,
                    url: attachment.url,
                    parent_guid: projectFilesRootGuid,
                    hierarchy_position:
                      Math.max(0, ...currentData.files.map(file => file.hierarchy_position)) + 1,
                    // The file keeps the element of its page
                    element_guid: data.element_guid,
                    type: 'file',
                  },
                ],
              },
          )
        }
        queryClient.invalidateQueries(getProjectFiles.getKey(projectId, projectFilesRootGuid))
        // ... which the "Dateien" templates list
        queryClient.invalidateQueries(
          getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
        )

        onUploadDocument?.(data)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hochladen'), { variant: 'error' })
      },
    },
  )
}

export default useUploadProjectDocumentationFile
